import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Button,
  Chip,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import DocumentSigningCreateDrawer from './DocumentSigningCreateDrawer';
import DocumentSigningDetailsDrawer from './DocumentSigningDetailsDrawer';

const STATE_CHIP_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Draft: 'default',
  FieldsConfigured: 'info',
  PendingSignatures: 'warning',
  SigningInProgress: 'warning',
  SigningCompleted: 'info',
  PdfEmbedding: 'info',
  Completed: 'success',
  Rejected: 'error',
  Cancelled: 'error',
};

const DocumentSigningPage: React.FC = () => {
  const { documentSigningApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();

  const [documents, setDocuments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [errors, setErrors] = useState({});

  const [paginationModel, setPaginationModel] = usePaginationPreference('documentSigning', 25);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentSigningApiWrapper.searchDocuments('1.0', term || undefined, paginationModel.page + 1, paginationModel.pageSize);
      setDocuments(res.data?.data?.queryResult ?? []);
      setTotal(res.data?.data?.totalRecords ?? 0);
    } catch (err) {
      handleAPIError(err, toast, errors);
    } finally {
      setLoading(false);
    }
  }, [term, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: intl.get('documentSigning.title') || 'Title', flex: 1 },
    {
      field: 'workflowState',
      headerName: intl.get('documentSigning.status') || 'Status',
      width: 180,
      renderCell: (params) => {
        const stateName: string = params.row.workflowState?.name ?? '';
        const color = STATE_CHIP_COLOR[stateName] ?? 'default';
        return <Chip label={intl.get(`documentSigning.workflowState.${stateName}`) || stateName} color={color} size="small" />;
      },
    },
    {
      field: 'signingOrder',
      headerName: intl.get('documentSigning.signingOrder') || 'Order',
      width: 120,
      renderCell: (params) => params.value ? intl.get('documentSigning.sequential') || 'Sequential' : intl.get('documentSigning.parallel') || 'Parallel',
    },
    {
      field: 'created_date',
      headerName: intl.get('common.createdDate') || 'Created',
      width: 160,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : '',
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={ENTITY_TYPE.ANY}
          onEdit={() => setSelectedId(params.row.id)}
          editScopes={[]}
        />
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">{intl.get('documentSigning.pageTitle') || 'Document Signing'}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {intl.get('documentSigning.upload') || 'New Document'}
          </Button>
        </Box>

        <TextField
          size="small"
          placeholder={intl.get('common.search') || 'Search...'}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ mb: 2, width: 320 }}
        />

        {loading ? (
          <Spinner />
        ) : (
          <DataGrid
            rows={documents}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        )}

        <DocumentSigningCreateDrawer
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); fetchDocuments(); }}
        />

        {selectedId !== null && (
          <DocumentSigningDetailsDrawer
            documentId={selectedId}
            open={selectedId !== null}
            onClose={() => setSelectedId(null)}
            onRefresh={fetchDocuments}
          />
        )}
      </Box>
    </Card>
  );
};

export default DocumentSigningPage;
